import { Table } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { FileText, GitBranch, MapPin, Radio, Users, Warehouse, type LucideIcon } from 'lucide-react'
import { LookupBar } from '@/components/common/LookupBar'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { C2C1FlowGraph } from '@/components/mapping/C2C1FlowGraph'
import { dashboardService } from '@/services'
import type { ContractsCustomerMapping } from '@/types'
import { cn } from '@/utils/cn'

function MappingStat({
  title,
  value,
  icon: Icon,
  mono,
}: {
  title: string
  value: string | number
  icon: LucideIcon
  mono?: boolean
}) {
  return (
    <div className="overview-kpi">
      <div className="overview-kpi__title">
        <Icon className="text-primary" size={14} />
        <span>{title}</span>
      </div>
      <div
        className={cn(
          'truncate text-2xl font-bold leading-tight tracking-tight text-[var(--foreground)]',
          mono && 'font-mono text-xl'
        )}
        title={String(value)}
      >
        {value}
      </div>
    </div>
  )
}

export default function C2C1MappingPage() {
  const [params, setParams] = useSearchParams()
  const bpCode = params.get('bp') || ''
  const [input, setInput] = useState(bpCode)

  useEffect(() => {
    setInput(bpCode)
  }, [bpCode])

  const q = useQuery({
    queryKey: ['c2-c1-mapping', bpCode],
    queryFn: () => dashboardService.getC2C1Mapping(bpCode),
    enabled: Boolean(bpCode),
  })

  const load = (value: string) => {
    const raw = value.trim()
    // Match BE canonicalization so padded SAP BPs (0010002875) hit the same node ids as contract C2s.
    const next = /^\d+$/.test(raw) ? raw.replace(/^0+/, '') || '0' : raw
    setInput(next)
    setParams(next ? { bp: next } : {})
  }

  return (
    <PageContainer fluid>
      <PageHeader
        title="Asset Life Cycle"
        subtitle="Visualise partner and plant paths across the RFID control tower"
      />

      <SectionCard
        title="Lookup"
        subtitle="C1s of the BP are fetched recursively. Downstream: CUSTOUT→LEAP when present, otherwise CPI GetFlow_ToCustomers carry-forward until a LEAP return appears."
      >
        <LookupBar
          type="sender"
          value={input}
          placeholder="End-user BP / C2 code (e.g. 10002863)"
          submitLabel="Map"
          onChange={setInput}
          onSubmit={load}
        />
      </SectionCard>

      {!bpCode ? (
        <PageEmpty
          icon={<GitBranch size={28} className="text-primary opacity-80" />}
          description="Enter an end-user BP to load the recursive C1 → C2 → LEAP plant journey."
        />
      ) : null}

      {bpCode && q.isLoading ? (
        <PageLoading tip="Expanding contract chain and LEAP plant origins…" />
      ) : null}

      {bpCode && q.isError ? (
        <PageError
          message="Could not load asset life-cycle mapping"
          description={(q.error as Error)?.message || 'SAP / OData request failed'}
        />
      ) : null}

      {bpCode && q.data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <MappingStat title="BP code" value={q.data.bpCode} icon={MapPin} mono />
            <MappingStat title="Parties" value={q.data.nodes?.length ?? 0} icon={Users} />
            <MappingStat
              title="LEAP plants"
              value={q.data.plantCount ?? q.data.plants?.length ?? 0}
              icon={Warehouse}
            />
            <MappingStat title="Contracts" value={q.data.contractCount} icon={FileText} />
            <MappingStat title="Chain depth" value={q.data.maxDepth ?? 0} icon={GitBranch} />
            <MappingStat
              title="Scannable tags"
              value={q.data.totalScannableTags ?? 0}
              icon={Radio}
            />
          </div>

          <SectionCard
            title="Flow"
            subtitle="Left → right: LEAP origin → C1 chain → end-user C2 → next customers (GetFlow) and/or LEAP return. Amber dashed = CPI flow; green dashed = CUSTOUT return; grey dashed = LEAPOUT origin."
            count={q.data.edges?.length ?? q.data.mappings.length}
          >
            <C2C1FlowGraph payload={q.data} />
          </SectionCard>

          <SectionCard
            title="Mapping rows"
            subtitle="Contract and plant edges with scannable tag counts along the journey."
            count={q.data.mappings.length}
          >
            <Table<ContractsCustomerMapping>
              size="small"
              rowKey={(r) =>
                `${r.edgeKind ?? 'contract'}-${r.contract}-${r.c2Customer}-${r.c1Customer}`
              }
              dataSource={q.data.mappings}
              pagination={{ pageSize: 25, hideOnSinglePage: true }}
              scroll={{ x: true }}
              columns={[
                {
                  title: 'Type',
                  dataIndex: 'edgeKind',
                  key: 'edgeKind',
                  width: 100,
                  render: (v: string | undefined) =>
                    v === 'plant'
                      ? 'LEAP origin'
                      : v === 'plant_return'
                        ? 'LEAP return'
                        : v === 'flow_forward'
                          ? 'CPI flow'
                          : 'Contract',
                  filters: [
                    { text: 'Contract', value: 'contract' },
                    { text: 'LEAP origin', value: 'plant' },
                    { text: 'LEAP return', value: 'plant_return' },
                    { text: 'CPI flow', value: 'flow_forward' },
                  ],
                  onFilter: (value, record) => (record.edgeKind ?? 'contract') === value,
                },
                {
                  title: 'Contract',
                  dataIndex: 'contract',
                  key: 'contract',
                  width: 140,
                  render: (v: string) => v || '—',
                },
                { title: 'From', dataIndex: 'c1Customer', key: 'c1Customer', width: 120 },
                {
                  title: 'From name',
                  dataIndex: 'c1CustomerName',
                  key: 'c1CustomerName',
                  ellipsis: true,
                },
                { title: 'To', dataIndex: 'c2Customer', key: 'c2Customer', width: 120 },
                {
                  title: 'To name',
                  dataIndex: 'c2CustomerName',
                  key: 'c2CustomerName',
                  ellipsis: true,
                },
                {
                  title: 'Depth',
                  dataIndex: 'depth',
                  key: 'depth',
                  width: 80,
                  align: 'right',
                },
                {
                  title: 'Scannable tags',
                  dataIndex: 'scannableTagCount',
                  key: 'scannableTagCount',
                  width: 130,
                  align: 'right',
                  sorter: (a, b) => (a.scannableTagCount ?? 0) - (b.scannableTagCount ?? 0),
                  defaultSortOrder: 'descend',
                },
              ]}
              locale={{ emptyText: 'No contract or plant mappings for this BP.' }}
            />
          </SectionCard>
        </>
      ) : null}
    </PageContainer>
  )
}
